import { TextField, ValidationErrors } from "@compass/core";
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderOptionState,
  BoxProps,
  SxProps,
  Theme,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { displayErrorMessages, getErrorMessages } from "./input-functions";

// here we are not extending the because we are expanding on more than one MUI components
// lets just define properties here as we need
export type SelectBaseProps<T, Multiple extends boolean = false> = Omit<BoxProps, "children" | "onChange"> & {
  multiple?: Multiple;
  value?: Multiple extends true ? T[] : T;
  onChange?: (
    event: React.SyntheticEvent,
    value: Multiple extends true ? T[] : T,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void;
  inputSx?: SxProps<Theme>;
  getOptionLabel?: (option: T) => string;
  groupBy?: (option: T) => string | undefined;
  label?: string;
  required?: boolean;
  errors?: ValidationErrors | string[];
  options?: T[];
  defaultValue?: any;
  disabled?: boolean;
  //disableClearable?: boolean;
  isOptionEqualToValue?: ((option: T, value: T) => boolean) | undefined;
  fetchFunction?: (searchString: string) => Promise<T[]>;
  placeholder?: string;
  autoComplete?: string;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  freeSolo?: boolean;
  size?: "small" | "medium" | undefined;
  open?: boolean;
  onInputChange?: (e: any, value: string) => void;
  popupIcon?: React.ReactNode;
  inputRef?: React.RefObject<HTMLInputElement>;
  inputProps?: any;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  autoFocus?: boolean;
  inputValue?: string;
  noOptionsText?: ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  readOnly?: boolean;
};

export const SelectBase = <T, Multiple extends boolean = false>(props: SelectBaseProps<T, Multiple>) => {
  const [typeDelayTimeoutId, setTypeDelayTimeoutId] = useState<any>();
  const [fetchOptions, setFetchOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // do some checking here
  if (props.options && props.fetchFunction) {
    throw new Error("options and fetchFunction props cannot be used together.");
  }

  const errorMsg = getErrorMessages(props.errors);
  const hasError = errorMsg?.length > 0 ? true : false;
  const isFetch = props.fetchFunction ? true : false; // this indicates whether we are grabbing the data from API

  const handleInputChange = (e, value: string) => {
    if (isFetch) {
      setLoading(true);
      setFetchOptions([]);

      clearTimeout(typeDelayTimeoutId);
      const _typeDelayTimeoutId = setTimeout(function () {
        getFetchOptions(value);
      }, 500); // wait for user to finish typing input: how many ms to wait
      setTypeDelayTimeoutId(_typeDelayTimeoutId);
    }

    if (props.onInputChange) props.onInputChange(e, value);
  };

  const getFetchOptions = (searchString: string) => {
    props.fetchFunction?.(searchString).then((response: T[]) => {
      setFetchOptions(response);
      setLoading(false);
    });
  };

  const options = isFetch ? fetchOptions || [] : props.options || [];

  const onChange = (
    event: React.SyntheticEvent,
    value: Multiple extends true ? T[] : T,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => {
    props.onChange?.(event, value, reason, details);
  };

  // everytime we focus, we search without a searching string, showing all options
  // this also triggers a search everytime we focus, so if we have some filtering going on that filter will be immediately applied
  // for example, look at StudentGrade's class which can be filtered by school id
  const handleFocus = (e) => {
    props.onFocus?.(e);

    if (isFetch) getFetchOptions("");
  };

  let _placeholder = props.placeholder;

  if (isFetch) {
    if (props.placeholder) _placeholder = `${props.placeholder} (search to get more options)`;
    else _placeholder = "Search to get more options";
  }

  return (
    <Autocomplete
      readOnly={props.readOnly}
      disablePortal={false}
      multiple={props.multiple}
      // @ts-ignore
      value={props.value || (props.multiple ? [] : null)}
      fullWidth
      options={options}
      // @ts-ignore
      getOptionLabel={props.getOptionLabel}
      groupBy={(t) => props.groupBy?.(t)!}
      renderOption={props.renderOption}
      filterOptions={isFetch ? (x) => x : undefined} // need this for isFetch so it doesnt try to filter the options from the Frontend
      disabled={props.disabled}
      inputValue={props.freeSolo ? props.inputValue || "" : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={props.inputSx}
          autoFocus={props.autoFocus}
          label={props.label}
          error={hasError}
          helperText={displayErrorMessages(errorMsg)}
          required={props.required}
          disabled={props.disabled}
          autoComplete={props.autoComplete}
          placeholder={_placeholder}
          size={props.size}
          inputRef={props.inputRef ? props.inputRef : undefined}
          inputProps={{
            ...params.inputProps,
            ...props.inputProps
          }}
        />
      )}
      disableClearable={undefined} // always show clearable: the calling function will need to handle setting to possibly undefined to prevent error
      // @ts-ignore
      onChange={onChange}
      onInputChange={(e, value) => handleInputChange(e, value)}
      loading={loading}
      isOptionEqualToValue={props.isOptionEqualToValue}
      disableCloseOnSelect={props.multiple ? true : false}
      freeSolo={props.freeSolo}
      open={props.open}
      onBlur={props.onBlur}
      onFocus={handleFocus}
      autoHighlight={!props.freeSolo}
      selectOnFocus={!props.freeSolo}
      noOptionsText={props.noOptionsText ? props.noOptionsText : undefined}
      getOptionDisabled={props.getOptionDisabled}
      sx={props.sx}
    />
  );
};
